import { Outlet, useNavigate, useSearchParams } from "react-router-dom"
import { normalizeRedirectURLForBasename } from "../../data-utils/authUtils.js"
import ErrorPage from "../../components/Errors/error-page"
import { useEffect, useRef } from "react"

import { AuthProvider, AUTH0_ERROR_MESSAGES } from "../auth/AuthProvider"
import { isKeycloak, authIssuer, authClientId } from "../auth/runtime-config"

/**
 * Mounts the configured IdP provider and intercepts denied-access redirects.
 *
 * The file keeps its original path and export name so App.routes.js and the existing
 * tests are untouched; the provider-specific wiring now lives in ../auth/AuthProvider.
 */

const SESSION_KEY = "ct_auth_error"

export const Auth0ProviderWithNavigate = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const logoutTriggered = useRef(false)

  // Intercept an IdP access_denied BEFORE the provider initializes, otherwise the
  // route guard immediately redirects back to the IdP and the user is stuck in a
  // login loop with no way to read the error.
  const error = searchParams.get("error")
  const errorDesc = searchParams.get("error_description")
  const isAccessDenied = error === "access_denied" || error === "unauthorized"
  const storedError = sessionStorage.getItem(SESSION_KEY)

  useEffect(() => {
    if (!isAccessDenied || logoutTriggered.current) return
    logoutTriggered.current = true

    const friendlyMessage = AUTH0_ERROR_MESSAGES[errorDesc] || errorDesc || "Access denied. Contact your ChaosTrack administrator."
    // Survives the logout redirect.
    sessionStorage.setItem(SESSION_KEY, friendlyMessage)

    const basename = process.env.PUBLIC_URL ?? "/"
    const returnTo = `${window.location.origin}${basename}`
    const clientId = authClientId()
    const issuer = authIssuer()

    // End-session endpoints differ: Auth0 uses /v2/logout?client_id=&returnTo=,
    // Keycloak uses the OIDC RP-initiated logout endpoint with
    // post_logout_redirect_uri. Built by hand here because the SDKs are not mounted
    // yet at this point in the render.
    const logoutUrl = isKeycloak()
      ? `${issuer}/protocol/openid-connect/logout?client_id=${encodeURIComponent(clientId)}&post_logout_redirect_uri=${encodeURIComponent(returnTo)}`
      : `${issuer}/v2/logout?client_id=${encodeURIComponent(clientId)}&returnTo=${encodeURIComponent(returnTo)}`

    window.location.replace(logoutUrl)
  }, [isAccessDenied, errorDesc])

  if (storedError) {
    sessionStorage.removeItem(SESSION_KEY)
    return <ErrorPage title="Access Denied" message={storedError} />
  }

  if (isAccessDenied) {
    return <ErrorPage title="Access Denied" message={AUTH0_ERROR_MESSAGES[errorDesc] || errorDesc || "Access denied."} />
  }

  const onRedirectCallback = (appState) => {
    navigate(normalizeRedirectURLForBasename(appState?.returnTo || window.location.pathname))
  }

  return (
    <AuthProvider onRedirectCallback={onRedirectCallback}>
      <Outlet />
    </AuthProvider>
  )
}
